<template>
  <template v-if="ingredientCount > ingredientSearchStore.perPage">
    <template v-if="!bulkActionsExpanded">
      <button
        id="ingredient-bulk-actions-button"
        class="button is-info is-outlined"
        @click="bulkActionsExpanded = true"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="fas fa-ellipsis-h"></i>
          </span>
          <span>Actions</span>
        </span>
      </button>
    </template>

    <div
      class="is-inline-flex gap-2 is-flex-wrap-wrap"
      :class="bulkActionsExpanded ? '' : 'is-hidden'"
    >
      <BulkArchiveButtons
        :store="ingredientSearchStore"
        entity-label="ingredient"
        hint-event-name="ingredient-bulk-selection:hint"
        @archive="openBulkArchiveModal(false)"
        @unarchive="openBulkArchiveModal(true)"
      >
        <template #delete>
          <BulkDeleteButton
            :store="ingredientSearchStore"
            :visible-records="ingredientSearchStore.visibleIngredients"
            entity-label="ingredient"
            :delete-endpoint="bulkIngredientDeletionsUserPath(ingredientSearchStore.userId)"
            @deleted="handleDeleted"
            @interrupted="refreshList"
          />
        </template>
      </BulkArchiveButtons>

      <BulkTagsButton
        :ids="ingredientSearchStore.selection"
        :count="count"
        :tag-store="ingredientTagStore"
        entity-label="ingredient"
        hint-event-name="ingredient-bulk-selection:hint"
        @applied="ingredientSearchStore.loadInitial({ force: true })"
      />
    </div>
  </template>
</template>

<script setup>
  import { computed } from "vue";

  import { Flash } from "~/jose_modern/flash";
  import { bulkIngredientDeletionsUserPath } from "~/routes";

  import BulkArchiveButtons from "../bulk-archive-buttons.vue";
  import BulkDeleteButton from "../bulk-delete-button.vue";
  import BulkTagsButton from "../bulk-tags-button.vue";
  import {
    useAutoExpandActions,
    provideBulkActionsPanelGroup,
    DELETION_FLASH_DURATION,
  } from "../lib/use-bulk-actions.js";

  provideBulkActionsPanelGroup();

  const props = defineProps({
    ingredientCount: { type: Number, default: Infinity },
    ingredientSearchStore: { type: Object, required: true },
    ingredientTagStore: { type: Object, required: true },
  });

  const count = computed(() => props.ingredientSearchStore.selection.length);
  const bulkActionsExpanded = useAutoExpandActions(count);

  function openBulkArchiveModal(isArchived) {
    const event = new CustomEvent("open-archive-modal", {
      detail: {
        context: "ingredient",
        bulkIds: props.ingredientSearchStore.selection,
        bulkCount: count.value,
        isArchived: isArchived,
        userId: props.ingredientSearchStore.userId,
        userHasInventoryPermission:
          props.ingredientSearchStore.visibleIngredients[0]?.user_has_inventory_permission ?? false,
      },
      bubbles: true,
      composed: true, // Allow event to cross Vue boundary
    });
    document.dispatchEvent(event);
  }

  async function handleDeleted(message) {
    // The message can say nothing was deleted, so a success flash would contradict it.
    Flash.notice(message || "Ingredients deleted.", { duration: DELETION_FLASH_DURATION });
    await refreshList();
  }

  async function refreshList() {
    props.ingredientSearchStore.clearSelection();
    await props.ingredientSearchStore.loadIngredientPage(1);
  }
</script>
